import React, { useState } from "react"
import {
  Card,
  Col,
  Row,
  CardBody,
  CardTitle,
  Label,
  Form,
  Input,
  FormFeedback,
  Button,
} from "reactstrap"

import { useFormik } from "formik"
import * as Yup from "yup"
import toast from "react-hot-toast"
import {
  useCreateUserMutation,
  useUpdateUserMutation,
} from "store/api/user/user.api"
import { Link, useNavigate } from "react-router-dom"
import Spinners from "components/Common/Spinner"
import { useGetAllRolesQuery } from "store/api/roles/roles.api"

const UsersForm = ({
  defaultValues,
  id = null,
  userStatus = null,
  HeaderComponent = null,
}) => {
  const [createUser, { isLoading }] = useCreateUserMutation()
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation()
  const { data } = useGetAllRolesQuery()
  const roles = data?.data || []
  const history = useNavigate()
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { ...defaultValues },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      if (id) await onUpdateUser(values)
      else await onCreateUser(values)
    },
  })
  document.title = "Create User | Prachay"

  const onCreateUser = async (values) => {
    try {
      const { data, message, success } = await createUser(values).unwrap()
      if (success) {
        history("/user-management#USER")
        toast.success(message, { id: "submitSuccess" })
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const onUpdateUser = async (values) => {
    try {
      const { data, message, success } = await updateUser({
        id,
        ...values,
      }).unwrap()
      if (success) {
        history("/user-management#USER")
        toast.success(message, { id: "submitSuccess" })
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }

      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardBody>
              {HeaderComponent && <HeaderComponent />}
              <Form
                className="form-horizontal"
                onSubmit={(e) => {
                  e.preventDefault()
                  validation.handleSubmit()
                  return false
                }}
              >
                <Row className="mb-2">
                  <Col xl={6}>
                    <Label className="col-form-label">
                      Name <span className="error">*</span>
                    </Label>
                    <Input
                      name="name"
                      className="form-control"
                      placeholder="Enter Name"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.name || ""}
                      invalid={
                        validation.touched.name && validation.errors.name
                          ? true
                          : false
                      }
                    />
                    {validation.touched.name && validation.errors.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </Col>
                  <Col xl={6}>
                    <Label className="col-form-label">Role</Label>
                    <select
                      name="role_id"
                      className={`form-select ${
                        validation.touched.role_id &&
                        validation.errors.role_id &&
                        "is-invalid"
                      }`}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation?.values?.role_id || ""}
                    >
                      <option value={""}>Select Role</option>
                      {roles.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {validation.touched.role_id && validation.errors.role_id ? (
                      <FormFeedback type="invalid">
                        {validation.errors.role_id}
                      </FormFeedback>
                    ) : null}
                  </Col>
                  <Col xl={6}>
                    <Label className="col-form-label">
                      Email Address<span className="error">*</span>
                    </Label>
                    <Input
                      name="email"
                      className="form-control"
                      placeholder="Enter Email Address"
                      type="text"
                      disabled={userStatus}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                      invalid={
                        validation.touched.email && validation.errors.email
                          ? true
                          : false
                      }
                    />
                    {validation.touched.email && validation.errors.email ? (
                      <FormFeedback type="invalid">
                        {validation.errors.email}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="justify-content-end mt-4">
                  <Col sm={12}>
                    <div className="d-flex justify-content-end align-items-center gap-3">
                      <Link
                        to={"/user-management#USER"}
                        type="button"
                        className="btn btn-cancel w-md"
                      >
                        Cancel
                      </Link>
                      <Button
                        type="submit"
                        className="btn btn-primary w-md"
                        disabled={isLoading || isUpdating}
                      >
                        {isLoading || isUpdating ? (
                          <Spinners />
                        ) : !id ? (
                          `Create User`
                        ) : (
                          "Update User"
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default UsersForm
